import { useEffect, useReducer, useRef } from 'react'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Plain text or existing HTML → TipTap document HTML. */
export function valueToCellHtml(value) {
  const raw = String(value || '').trim()
  if (!raw) return '<p></p>'
  if (/<[a-z][\s\S]*>/i.test(raw)) return raw
  const paragraphs = raw.split(/\n{2,}/).map((p) => {
    const line = escapeHtml(p).replace(/\n/g, '<br>')
    return `<p>${line || '<br>'}</p>`
  })
  return paragraphs.join('') || '<p></p>'
}

function looksLikeHtml(value) {
  return /<[a-z][\s\S]*>/i.test(String(value || ''))
}

/** Light strip for read-only display (scripts/handlers). */
export function sanitiseCellHtml(html) {
  return String(html || '')
    .replace(/<(script|iframe|object|embed)[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/\son\w+\s*=\s*(['"])[\s\S]*?\1/gi, '')
}

export function WideCellDisplay({ value }) {
  if (!value) return <span className="text-slate-400">—</span>
  if (looksLikeHtml(value)) {
    return (
      <div
        className="prose-kb max-w-xl text-sm leading-relaxed [&_p]:my-1"
        dangerouslySetInnerHTML={{ __html: sanitiseCellHtml(value) }}
      />
    )
  }
  return (
    <span className="block max-w-xl whitespace-pre-wrap break-words text-sm leading-relaxed">
      {value}
    </span>
  )
}

function ToolbarButton({ active, onClick, children, title }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={[
        'rounded border px-2 py-1 text-xs font-semibold',
        active
          ? 'border-brand bg-brand text-white'
          : 'border-slate-300 bg-white text-brand hover:bg-slate-50',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

/**
 * Large multi-line cell editor with Bold / Underline (stores HTML).
 */
export function WideCellEditor({ value, onChange, placeholder }) {
  const [, bump] = useReducer((n) => n + 1, 0)
  const lastEmitted = useRef(null)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        orderedList: false,
        bulletList: false,
      }),
      Underline,
    ],
    content: valueToCellHtml(value),
    editorProps: {
      attributes: {
        class:
          'prose-kb min-h-[9rem] max-w-none px-3 py-2 text-sm leading-relaxed focus:outline-none',
        ...(placeholder ? { 'data-placeholder': placeholder } : {}),
      },
    },
    onSelectionUpdate: () => bump(),
    onTransaction: () => bump(),
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML()
      lastEmitted.current = html
      onChange?.(html === '<p></p>' ? '' : html)
    },
  })

  useEffect(() => {
    if (!editor) return
    const incoming = valueToCellHtml(value)
    if (incoming === lastEmitted.current) return
    const current = editor.getHTML()
    if (current !== incoming) {
      editor.commands.setContent(incoming, false)
      lastEmitted.current = incoming
    }
  }, [editor, value])

  if (!editor) {
    return (
      <div className="min-h-[9rem] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-brand-muted">
        Loading editor…
      </div>
    )
  }

  return (
    <div className="min-w-[18rem] max-w-xl overflow-hidden rounded-md border border-slate-300 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
        <ToolbarButton
          title="Bold"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          Bold
        </ToolbarButton>
        <ToolbarButton
          title="Underline"
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          Underline
        </ToolbarButton>
        <ToolbarButton
          title="Italic"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          Italic
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  )
}
